/**
 * HMRC fraud-prevention client headers for advisor-portal → API → HMRC calls.
 * Kept dependency-free (no crypto-js); unique-reference is a stable 64-char hex digest.
 */

const DEVICE_COOKIE = "uid";
const MFA_TS_KEY = "hmrcMfaTimestamp";
const MFA_REF_KEY = "hmrcMfaUniqueReference";

function pad(number, length) {
  let str = "" + number;
  while (str.length < length) {
    str = "0" + str;
  }
  return str;
}

function uuidv4() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Deterministic 64-char uppercase hex (stable across calls for the same input). */
function stableHexDigest(message) {
  const str = String(message);
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  let h3 = 0xdeadbeef;
  let h4 = 0x85ebca6b;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ c, 0x811c9dc5) >>> 0;
    h3 = Math.imul(h3 ^ (c << 1), 0x01000193) >>> 0;
    h4 = Math.imul(h4 ^ (c << 2), 0x85ebca6b) >>> 0;
  }
  const parts = [h1, h2, h3, h4, h1 ^ h3, h2 ^ h4, ~h1 >>> 0, ~h2 >>> 0];
  return parts
    .map((n) => ("00000000" + (n >>> 0).toString(16)).slice(-8))
    .join("")
    .toUpperCase();
}

function getCookie(cName) {
  const name = cName + "=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

function setCookie(cName, cValue, exDays) {
  const d = new Date();
  d.setTime(d.getTime() + exDays * 24 * 60 * 60 * 1000);
  document.cookie =
    cName + "=" + cValue + ";expires=" + d.toUTCString() + ";path=/";
}

function getTimezoneOffset() {
  const offset = new Date().getTimezoneOffset();
  return (
    (offset < 0 ? "+" : "-") +
    pad(parseInt(Math.abs(offset / 60), 10), 2) +
    ":" +
    pad(Math.abs(offset % 60), 2)
  );
}

function getPositiveWindowDimensions() {
  const toPositiveInteger = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.round(n);
  };

  const width =
    toPositiveInteger(window.visualViewport && window.visualViewport.width) ||
    toPositiveInteger(window.innerWidth) ||
    toPositiveInteger(
      document.documentElement && document.documentElement.clientWidth
    ) ||
    toPositiveInteger(window.screen && window.screen.width) ||
    1024;
  const height =
    toPositiveInteger(window.visualViewport && window.visualViewport.height) ||
    toPositiveInteger(window.innerHeight) ||
    toPositiveInteger(
      document.documentElement && document.documentElement.clientHeight
    ) ||
    toPositiveInteger(window.screen && window.screen.height) ||
    960;

  return { width, height };
}

function hmrcMfaIsoUtc(date) {
  const d = date || new Date();
  return d.toISOString().replace(/\.\d{3}Z$/, "Z");
}

/** Call on successful advisor login. */
export function recordHmrcMfaAtLogin(agentId) {
  const loginIso = hmrcMfaIsoUtc();
  const id = String(agentId || localStorage.getItem("agent") || "");
  const uniqueRef = stableHexDigest(id + ":" + loginIso);
  localStorage.setItem(MFA_TS_KEY, loginIso);
  localStorage.setItem(MFA_REF_KEY, uniqueRef);
}

function getGovClientMultiFactorHeader() {
  let iso = localStorage.getItem(MFA_TS_KEY);
  let uniqueRef = localStorage.getItem(MFA_REF_KEY) || "";
  const agentId = localStorage.getItem("agent") || "";
  const fallbackId = agentId || getCookie(DEVICE_COOKIE) || "untied-advisor";

  if (!iso) {
    iso = hmrcMfaIsoUtc();
  }
  if (!uniqueRef) {
    uniqueRef = stableHexDigest(fallbackId + ":" + iso);
    localStorage.setItem(MFA_TS_KEY, iso);
    localStorage.setItem(MFA_REF_KEY, uniqueRef);
  }

  const encodedTs = iso.replace(/:/g, "%3A");
  return (
    "Gov-Client-Multi-Factor: type=TOTP&timestamp=" +
    encodedTs +
    "&unique-reference=" +
    uniqueRef
  );
}

function getDeviceId() {
  let hmrcUid = getCookie(DEVICE_COOKIE);
  if (!hmrcUid) {
    hmrcUid = uuidv4();
  }
  setCookie(DEVICE_COOKIE, hmrcUid, 365);
  return hmrcUid;
}

export function getHMRCAFHeaders() {
  const agentId = localStorage.getItem("agent") || "";
  const array = [];

  array.push("Gov-Client-Device-ID: " + getDeviceId());
  array.push(
    "Gov-Client-User-IDs: untied-advisor=" + encodeURIComponent(agentId)
  );
  array.push(
    "Gov-Client-Screens: width=" +
      window.screen.width +
      "&height=" +
      window.screen.height +
      "&scaling-factor=" +
      ((window.visualViewport && window.visualViewport.scale) || 1) +
      "&colour-depth=" +
      window.screen.colorDepth
  );
  const { width, height } = getPositiveWindowDimensions();
  array.push("Gov-Client-Window-Size: width=" + width + "&height=" + height);
  array.push("Gov-Client-Timezone: UTC" + getTimezoneOffset());

  let pluginsStr = "";
  try {
    const plugins = navigator.plugins;
    for (let i = 0; i < plugins.length; i++) {
      pluginsStr +=
        encodeURI(plugins[i].name) + (i < plugins.length - 1 ? "," : "");
    }
  } catch (e) {
    // ignore
  }
  array.push("Gov-Client-Browser-Plugins: " + pluginsStr);
  array.push("Gov-Client-Browser-JS-User-Agent: " + navigator.userAgent);

  if (agentId) {
    const hashed = stableHexDigest(String(agentId));
    array.push(
      "Gov-Vendor-License-IDs: " +
        encodeURIComponent("untied") +
        "=" +
        encodeURIComponent(hashed)
    );
  }

  array.push(getGovClientMultiFactorHeader());
  return array;
}

export function govHeadersJson() {
  return JSON.stringify(getHMRCAFHeaders());
}
